import { useEffect, useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { Loader2, AlertTriangle, Package } from 'lucide-react'
import { useContextEngineStore } from '@/stores/contextEngineStore'
import { TemplateBrowser } from '@/components/context/TemplateBrowser'
import { ContextPackagePreview } from '@/components/context/ContextPackagePreview'
import type { ToolTemplate } from '@/types/contextEngine'

export function PackagesPage() {
  const {
    templates,
    templatesLoading,
    templatesError,
    generatedPackage,
    packageLoading,
    packageError,
    fetchTemplates,
    generatePackage,
    clearPackage,
  } = useContextEngineStore()

  const [selectedTemplate, setSelectedTemplate] = useState<ToolTemplate | null>(null)
  const [operation, setOperation] = useState('')

  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates])

  // Clear package and operation when template changes
  useEffect(() => {
    clearPackage()
    setOperation('')
  }, [selectedTemplate, clearPackage])

  const handleGenerate = useCallback(() => {
    if (!selectedTemplate) return
    generatePackage(
      selectedTemplate.templateId,
      operation || `Generate context for ${selectedTemplate.toolName}`,
    )
  }, [selectedTemplate, operation, generatePackage])


  // Loading templates
  if (templatesLoading && templates.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-accent-amber" size={32} />
      </div>
    )
  }

  // Error loading templates
  if (templatesError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center p-4">
        <AlertTriangle size={24} className="text-accent-error" />
        <p className="text-text-secondary">Couldn&apos;t load templates.</p>
        <button
          onClick={fetchTemplates}
          className="text-sm text-accent-amber hover:underline"
        >
          Retry
        </button>
      </div>
    )
  }

  // Empty — no templates
  if (templates.length === 0) {
    return (
      <div className="p-6 lg:p-8 max-w-[900px] mx-auto">
        <h1 className="font-display text-2xl font-bold text-text-primary mb-6">Packages</h1>
        <EmptyState />
      </div>
    )
  }

  return (
    <div className="p-6 lg:p-8 max-w-[900px] mx-auto space-y-8">
      <div>
        <h1 className="font-display text-2xl font-bold text-text-primary mb-1">Packages</h1>
        <p className="text-sm text-text-secondary">
          Generate scoped context packages for your tools.
        </p>
      </div>

      {/* Template browser */}
      <TemplateBrowser
        templates={templates}
        selectedId={selectedTemplate?.templateId ?? null}
        onSelect={setSelectedTemplate}
      />

      {/* Operation input + generate button */}
      {selectedTemplate && (
        <GenerateControls
          toolName={selectedTemplate.toolName}
          operation={operation}
          onOperationChange={setOperation}
          onGenerate={handleGenerate}
          loading={packageLoading}
        />
      )}

      {/* Error */}
      {packageError && (
        <div className="flex items-center gap-2 rounded-lg border border-accent-error/30 bg-accent-error/5 p-3 text-sm text-accent-error">
          <AlertTriangle size={16} />
          {packageError}
        </div>
      )}

      {/* Loading */}
      {packageLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="animate-spin text-accent-amber" size={28} />
            <p className="text-sm text-text-secondary">Generating context package…</p>
          </div>
        </div>
      )}

      {/* Preview */}
      {generatedPackage && !packageLoading && selectedTemplate && (
        <ContextPackagePreview
          package_={generatedPackage}
          toolName={selectedTemplate.toolName}
          onRegenerate={handleGenerate}
        />
      )}
    </div>
  )
}

// ─── Generate controls ───────────────────────────────────────────────────────

interface GenerateControlsProps {
  toolName: string
  operation: string
  onOperationChange: (value: string) => void
  onGenerate: () => void
  loading: boolean
}

function GenerateControls({ toolName, operation, onOperationChange, onGenerate, loading }: GenerateControlsProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="space-y-3"
    >
      <label className="block text-sm text-text-secondary">
        What are you working on? <span className="text-text-tertiary">(optional)</span>
      </label>
      <div className="flex gap-3">
        <input
          type="text"
          value={operation}
          onChange={(e) => onOperationChange(e.target.value)}
          placeholder={`e.g. "Building the onboarding flow" or "Writing investor update"`}
          className="flex-1 rounded-md border border-border bg-bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary outline-none focus:border-accent-amber transition-colors"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !loading) onGenerate()
          }}
        />
        <button
          onClick={onGenerate}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-md bg-accent-amber px-4 py-2 text-sm font-semibold text-bg-deepest transition-colors hover:bg-accent-amber-hover disabled:opacity-50"
        >
          Generate for {toolName}
        </button>
      </div>
    </motion.div>
  )
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.35 }}
      className="flex flex-col items-center justify-center py-16 text-center"
    >
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-accent-amber/10">
        <Package size={32} className="text-accent-amber" />
      </div>
      <h2 className="font-display text-lg font-semibold text-text-primary mb-1">
        No templates yet
      </h2>
      <p className="text-sm text-text-secondary max-w-sm">
        Generate a context package — choose a template and tool. Templates will appear as your context grows.
      </p>
    </motion.div>
  )
}
